import {
  buildSerpSnippetSegments,
  type SerpMisalignedLink,
  type SerpSnippetSegment
} from './serp-snippet-segments';

const MIN_WORD_LEN = 4;
const COLLOCATION_WINDOW = 3;

export interface SerpCollocationHead {
  head: string;
  neighbors: string[];
}

export type MisalignedPhraseLink = SerpMisalignedLink;

const FOLD_STEM_MIN = 3;

/** Light plural folding — keep in sync with backend `foldLexicalToken`. */
export function foldLexicalToken(token: string): string {
  let t = token.toLowerCase().replace(/[^a-z0-9]+/g, '');
  if (t.length < FOLD_STEM_MIN) return t;
  if (t.endsWith('ies') && t.length >= 5) {
    const stem = `${t.slice(0, -3)}y`;
    return stem.length >= FOLD_STEM_MIN ? stem : t;
  }
  if (t.endsWith('es') && t.length >= 5) {
    if (/(?:s|x|z|ch|sh)es$/.test(t)) {
      const stem = t.slice(0, -2);
      if (stem.length >= FOLD_STEM_MIN) return stem;
    }
    const stemS = t.slice(0, -1);
    if (stemS.endsWith('e') && stemS.length >= FOLD_STEM_MIN) return stemS;
    const stemEs = t.slice(0, -2);
    if (stemEs.length >= FOLD_STEM_MIN) return stemEs;
  }
  if (t.endsWith('s') && t.length > 4 && !t.endsWith('ss')) {
    const stem = t.slice(0, -1);
    return stem.length >= FOLD_STEM_MIN ? stem : t;
  }
  return t;
}

const LINK_STOPWORDS = new Set([
  'your', 'the', 'and', 'for', 'with', 'that', 'this', 'from', 'than', 'into', 'over',
  'after', 'all', 'who', 'can', 'been', 'have', 'more', 'past', 'due', 'nothing', 'faster',
  'roof', 'want', 'chase', 'dealing', 'missed', 'pretty', 'also', 'just', 'very', 'when',
  'what', 'how', 'are', 'was', 'were', 'will', 'would', 'could', 'should', 'about'
]);

function tokenize(text: string): string[] {
  return (text || '')
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, ' ')
    .split(/[\s-]+/)
    .map((t) => t.trim())
    .filter((t) => t.length >= 3);
}

interface TokenSpan {
  word: string;
  start: number;
  end: number;
  index: number;
}

function buildTokenSpans(raw: string, minLen: number): TokenSpan[] {
  const spans: TokenSpan[] = [];
  const wordRe = new RegExp(`\\b([a-zA-Z]{${minLen},})\\b`, 'g');
  let match: RegExpExecArray | null;
  let index = 0;
  while ((match = wordRe.exec(raw)) !== null) {
    spans.push({
      word: match[1],
      start: match.index,
      end: match.index + match[1].length,
      index: index++
    });
  }
  return spans;
}

function tokensInWindow(tokens: string[], centerIndex: number, window: number): string[] {
  const out: string[] = [];
  for (let i = Math.max(0, centerIndex - window); i <= Math.min(tokens.length - 1, centerIndex + window); i++) {
    if (i !== centerIndex) out.push(tokens[i]);
  }
  return out;
}

function pickActualNeighbor(
  tokens: string[],
  headIdx: number,
  headFold: string,
  expectedFolds: Set<string>
): { word: string; index: number } | null {
  let best: { word: string; index: number; dist: number; score: number } | null = null;

  for (let i = Math.max(0, headIdx - COLLOCATION_WINDOW); i <= Math.min(tokens.length - 1, headIdx + COLLOCATION_WINDOW); i++) {
    if (i === headIdx) continue;
    const word = tokens[i];
    const fold = foldLexicalToken(word);
    if (fold === headFold || expectedFolds.has(fold)) continue;

    const dist = Math.abs(i - headIdx);
    const stop = LINK_STOPWORDS.has(fold);
    const score = dist * 10 + (stop ? 50 : 0) - word.length;

    if (!best || score < best.score) {
      best = { word, index: i, dist, score };
    }
  }

  if (!best) {
    for (let i = Math.max(0, headIdx - COLLOCATION_WINDOW); i <= Math.min(tokens.length - 1, headIdx + COLLOCATION_WINDOW); i++) {
      if (i === headIdx) continue;
      const word = tokens[i];
      const fold = foldLexicalToken(word);
      if (fold === headFold || expectedFolds.has(fold)) continue;
      const dist = Math.abs(i - headIdx);
      if (!best || dist < best.dist) best = { word, index: i, dist, score: dist };
    }
  }

  return best ? { word: best.word, index: best.index } : null;
}

interface MisalignedOccurrence {
  headStart: number;
  headWord: string;
  partnerWord: string;
  expected: string;
}

function findMisalignedOccurrences(
  tokenSpans: TokenSpan[],
  tokens: string[],
  headByFold: Map<string, SerpCollocationHead>
): MisalignedOccurrence[] {
  const occurrences: MisalignedOccurrence[] = [];

  for (const span of tokenSpans) {
    const head = headByFold.get(foldLexicalToken(span.word));
    if (!head) continue;

    const expectedFolds = new Set(head.neighbors.map((n) => foldLexicalToken(n)));
    const windowFolds = new Set(
      tokensInWindow(tokens, span.index, COLLOCATION_WINDOW).map((t) => foldLexicalToken(t))
    );
    if ([...expectedFolds].some((n) => windowFolds.has(n))) continue;

    const actual = pickActualNeighbor(tokens, span.index, foldLexicalToken(span.word), expectedFolds);
    if (!actual) continue;

    occurrences.push({
      headStart: span.start,
      headWord: span.word,
      partnerWord: actual.word,
      expected: head.neighbors[0] || head.head
    });
  }

  return occurrences;
}

function linkAtHeadStart(
  occurrences: MisalignedOccurrence[],
  start: number
): SerpMisalignedLink | undefined {
  const occ = occurrences.find((o) => o.headStart === start);
  if (!occ) return undefined;
  return { head: occ.headWord, actual: occ.partnerWord, expected: occ.expected };
}

/** Green in winner snippets (phrase / substring match). */
export function buildSerpContractTermSegments(
  text: string,
  contractTerms: string[] | null | undefined
): SerpSnippetSegment[] {
  return buildSerpSnippetSegments(text, contractTerms);
}

/** Distinct misaligned phrase links for chips / summaries. */
export function collectMisalignedPhraseLinks(
  text: string,
  contractTerms: string[] | null | undefined,
  collocationHeads: SerpCollocationHead[] | null | undefined = []
): MisalignedPhraseLink[] {
  const raw = (text || '').trim();
  if (!raw) return [];

  const headByFold = new Map<string, SerpCollocationHead>();
  for (const head of collocationHeads || []) {
    headByFold.set(foldLexicalToken(head.head), head);
  }
  if (!headByFold.size) return [];

  const tokenSpans = buildTokenSpans(raw, 3);
  const occurrences = findMisalignedOccurrences(tokenSpans, tokenize(raw), headByFold);
  const seen = new Set<string>();
  const links: MisalignedPhraseLink[] = [];

  for (const occ of occurrences) {
    const key = `${foldLexicalToken(occ.headWord)}:${foldLexicalToken(occ.partnerWord)}`;
    if (seen.has(key)) continue;
    seen.add(key);
    links.push({
      head: occ.headWord,
      actual: occ.partnerWord,
      expected: occ.expected
    });
  }
  return links;
}

/** Green / amber on your page — phrase-aware; misaligned details are hover-only. */
export function buildYourPageVocabularySegments(
  text: string,
  contractTerms: string[] | null | undefined,
  collocationHeads: SerpCollocationHead[] | null | undefined = []
): SerpSnippetSegment[] {
  const raw = (text || '').trim();
  if (!raw) return [];

  const contractFolds = new Set(
    (contractTerms || [])
      .map((t) => foldLexicalToken(t))
      .filter((t) => t.length >= MIN_WORD_LEN)
  );
  const headByFold = new Map<string, SerpCollocationHead>();
  for (const head of collocationHeads || []) {
    headByFold.set(foldLexicalToken(head.head), head);
  }

  if (!contractFolds.size && !headByFold.size) {
    return [{ text: raw, emphasized: false }];
  }

  const minLen = headByFold.size ? 3 : MIN_WORD_LEN;
  const tokenSpans = buildTokenSpans(raw, minLen);
  const tokens = tokenize(raw);
  const misalignedOccurrences = findMisalignedOccurrences(tokenSpans, tokens, headByFold);
  const misalignedHeadStarts = new Set(misalignedOccurrences.map((o) => o.headStart));

  const segments: SerpSnippetSegment[] = [];
  const wordRe = new RegExp(`\\b([a-zA-Z]{${minLen},})\\b`, 'g');
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = wordRe.exec(raw)) !== null) {
    const start = match.index;
    const word = match[1];
    if (start > lastIndex) {
      segments.push({ text: raw.slice(lastIndex, start), emphasized: false });
    }

    const fold = foldLexicalToken(word);
    const head = headByFold.get(fold);
    const misalignedLink = linkAtHeadStart(misalignedOccurrences, start);

    if (misalignedLink) {
      segments.push({ text: word, emphasized: false, misaligned: true, misalignedLink });
      lastIndex = start + word.length;
      continue;
    }

    let usage: 'plain' | 'aligned' | 'misaligned' = 'plain';
    if (head) {
      const neighborFolds = new Set(head.neighbors.map((n) => foldLexicalToken(n)));
      const span = tokenSpans.find((s) => s.start === start);
      if (span) {
        const windowFolds = new Set(
          tokensInWindow(tokens, span.index, COLLOCATION_WINDOW).map((t) => foldLexicalToken(t))
        );
        if ([...neighborFolds].some((n) => windowFolds.has(n))) usage = 'aligned';
        else if (misalignedHeadStarts.has(start)) usage = 'misaligned';
      }
    } else if (contractFolds.has(fold)) {
      usage = 'aligned';
    }

    segments.push({
      text: word,
      emphasized: usage === 'aligned',
      misaligned: usage === 'misaligned'
    });
    lastIndex = start + word.length;
  }

  if (lastIndex < raw.length) {
    segments.push({ text: raw.slice(lastIndex), emphasized: false });
  }

  return segments.filter((seg) => seg.text.length > 0);
}
